<template>
  <div v-if="hasItems" :class="rootClasses">
    <!-- 下拉选择 header -->
    <div class="store-dropdown" :class="dropdownClasses">
      <div class="store-dropdown__header" @click="toggleDropdown">
        <span class="store-dropdown__name" :style="storeNameStyle">{{ currentStore.name }}</span>
        <span class="store-dropdown__total" @click.stop="toggleDropdown">
          {{ allStoresLabel }} ({{ items.length }})
          <svg
            class="store-dropdown__arrow"
            :class="{ 'store-dropdown__arrow--open': isDropdownOpen }"
            width="15"
            height="9"
            viewBox="0 0 18 11"
            fill="none"
          >
            <path
              d="M1.41016 1.41016L8.53976 9.71029L15.6694 1.41016"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="square"
              stroke-linejoin="round"
            />
          </svg>
        </span>
      </div>

      <!-- 下拉列表 -->
      <div v-show="isDropdownOpen" class="store-dropdown__list">
        <div
          v-for="(item, index) in items"
          :key="index"
          class="store-dropdown__item"
          :class="{ 'store-dropdown__item--active': index === currentIndex }"
          @click="selectStore(index)"
        >
          <svg
            class="store-dropdown__check"
            :class="{ 'invisible': index !== currentIndex }"
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
          >
            <path
              d="M2.5 7L5.5 10L11.5 4"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span
            class="store-dropdown__item-name"
            :class="{ 'font-bold': index === currentIndex }"
          >
            {{ item.name }}
          </span>
        </div>
      </div>
    </div>

    <!-- ==================== PC 布局 ==================== -->
    <template v-if="isPC">
      <!-- PC 有地图：图片 + 地图并排，信息在下方 -->
      <template v-if="currentStore.mapUrl">
        <div class="store-media store-media--with-map">
          <div v-if="currentStore.image" class="store-media__image">
            <img :src="currentStore.image" :alt="currentStore.name" />
          </div>
          <div class="store-media__map">
            <iframe
              :src="currentStore.mapUrl"
              width="100%"
              height="100%"
              style="border: 0"
              allowfullscreen
              loading="lazy"
              referrerpolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>

        <div class="store-info store-info--below" :style="infoTextStyle">
          <div v-if="currentStore.businessHours" class="store-info__row">
            <img class="store-info__icon" :src="iconHour" width="32" height="32" />
            <span class="store-info__text" v-html="currentStore.businessHours"></span>
          </div>
          <div v-if="currentStore.address" class="store-info__row">
            <img class="store-info__icon" :src="iconAddr" width="32" height="32" />
            <span class="store-info__text whitespace-pre-wrap" v-html="currentStore.address"></span>
          </div>
        </div>

        <!-- PC 有地图时按钮在底部居中 -->
        <div v-if="showBtn" class="store-action store-action--center">
          <button class="store-action__btn" :style="btnStyle" @click="handleGoClick">
            {{ props.buttonText }}
          </button>
        </div>
      </template>

      <!-- PC 无地图：图片 + 信息并排 -->
      <template v-else>
        <div class="store-media store-media--no-map">
          <div v-if="currentStore.image" class="store-media__image">
            <img :src="currentStore.image" :alt="currentStore.name" />
          </div>
          <div class="store-info store-info--side" :class="{ 'store-info--side-no-btn': !showBtn }" :style="infoTextStyle">
            <div v-if="currentStore.businessHours" class="store-info__row">
              <img class="store-info__icon" :src="iconHour" width="32" height="32" />
              <span class="store-info__content" v-html="currentStore.businessHours"></span>
            </div>
            <div v-if="currentStore.address" class="store-info__row">
              <img class="store-info__icon" :src="iconAddr" width="32" height="32" />
              <span class="store-info__content whitespace-pre-wrap" v-html="currentStore.address"></span>
            </div>

            <div v-if="showBtn" class="store-action store-action--inline">
              <button class="store-action__btn" :style="btnStyle" @click="handleGoClick">
                {{ props.buttonText }}
              </button>
            </div>
          </div>
        </div>
      </template>
    </template>

    <!-- ==================== H5 布局 ==================== -->
    <template v-else>
      <div class="store-body-mobile" :class="currentStore.mapUrl ? 'pb-[20px]' : 'pb-[30px]'">
        <!-- H5 图片始终全宽 -->
        <div v-if="currentStore.image" class="store-media__image store-media__image--mobile">
          <img :src="currentStore.image" :alt="currentStore.name" />
        </div>

        <!-- H5 信息区 -->
        <div class="store-info store-info--mobile" :style="infoTextStyle">
          <div v-if="currentStore.businessHours" class="store-info__row">
            <img class="store-info__icon" :src="iconHour" width="32" height="32" />
            <span class="store-info__content" v-html="currentStore.businessHours"></span>
          </div>
          <div v-if="currentStore.address" class="store-info__row">
            <img class="store-info__icon" :src="iconAddr" width="32" height="32" />
            <span class="store-info__content whitespace-pre-wrap" v-html="currentStore.address"></span>
          </div>
        </div>

        <!-- H5 按钮 -->
        <div v-if="showBtn" class="store-action store-action--mobile">
          <button class="store-action__btn store-action__btn--mobile" :style="btnStyle" @click="handleGoClick">
            {{ props.buttonText }}
          </button>
        </div>

        <!-- H5 地图始终在最底部 -->
        <div v-if="currentStore.mapUrl" class="store-map-mobile">
          <iframe
            :src="currentStore.mapUrl"
            width="100%"
            height="100%"
            style="border: 0"
            allowfullscreen
            loading="lazy"
            referrerpolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue-demi';
import { use as fegoUse } from '@fegobull/webapi';
import { useFegoPageInfo } from '@ark/web-shared/use';
import { BTN_CLICK_EVENT } from '../config';
import locales from './locales';
import iconHourLight from './images/icon-hour.png';
import iconAddrLight from './images/icon-addr.png';
import iconHourDark from './images/icon-hour-dark.png';
import iconAddrDark from './images/icon-addr-dark.png';
interface StoreItem {
  name: string;
  businessHours: string;
  address: string;
  mapUrl: string;
  mapLink: string;
  image: string;
}
const __sfc_main = {};
__sfc_main.props = {
  items: {
    key: "items",
    required: true,
    type: Array
  },
  showButton: {
    key: "showButton",
    required: false,
    type: Boolean
  },
  buttonText: {
    key: "buttonText",
    required: false,
    type: String
  },
  theme: {
    key: "theme",
    required: false,
    type: String
  },
  btnClickAction: {
    key: "btnClickAction",
    required: false,
    type: String
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const props = __props;
  const emit = __ctx.emit;
  const {
    kls,
    isPC
  } = useFegoPageInfo();
  const {
    useI18n
  } = fegoUse;
  const $t = useI18n(locales);
  const currentIndex = ref(0);
  const isDropdownOpen = ref(false);
  const hasItems = computed(() => props.items && props.items.length > 0);
  const currentStore = computed(() => props.items[currentIndex.value] || {} as StoreItem);
  const allStoresLabel = computed(() => $t('allStores'));
  const showBtn = computed(() => props.showButton !== false && !!props.buttonText);
  const isDark = computed(() => props.theme === 'dark');
  const iconHour = computed(() => isDark.value ? iconHourDark : iconHourLight);
  const iconAddr = computed(() => isDark.value ? iconAddrDark : iconAddrLight);
  const btnStyle = computed(() => ({
    backgroundColor: isDark.value ? '#ff6900' : '#282828',
    color: '#fff'
  }));
  const infoTextStyle = computed(() => ({
    color: isDark.value ? '#fff' : '#000'
  }));
  const storeNameStyle = computed(() => ({
    color: '#ff6900'
  }));
  const rootClasses = computed(() => ['store-container', isDark.value ? 'theme-dark' : 'theme-light', ...kls.value]);
  const dropdownClasses = computed(() => ({
    'store-dropdown--open': isDropdownOpen.value
  }));
  function toggleDropdown() {
    isDropdownOpen.value = !isDropdownOpen.value;
  }
  function selectStore(index: number) {
    currentIndex.value = index;
    isDropdownOpen.value = false;
  }
  function handleGoClick() {
    const store = currentStore.value;
    if (props.btnClickAction === 'map' && store.mapLink) {
      window.open(store.mapLink, '_blank');
    }
    emit(BTN_CLICK_EVENT.on, {
      store
    });
  }
  function handleClickOutside(e: MouseEvent) {
    const target = e.target as HTMLElement;
    if (!target.closest('.store-dropdown')) {
      isDropdownOpen.value = false;
    }
  }
  onMounted(() => {
    document.addEventListener('click', handleClickOutside);
  });
  onBeforeUnmount(() => {
    document.removeEventListener('click', handleClickOutside);
  });
  return {
    props,
    isPC,
    currentIndex,
    isDropdownOpen,
    hasItems,
    currentStore,
    allStoresLabel,
    showBtn,
    iconHour,
    iconAddr,
    btnStyle,
    infoTextStyle,
    storeNameStyle,
    rootClasses,
    dropdownClasses,
    toggleDropdown,
    selectStore,
    handleGoClick
  };
};
export default __sfc_main;
</script>

<style scoped>
@tailwind base;
@tailwind components;
@tailwind utilities;

.store-container {
  @apply w-full mx-auto box-border bg-white;
}
.store-container.is-pc {
  @apply max-w-[1180px] py-0 !overflow-visible;
}
.store-container.is-mobile {
  @apply px-[40px] py-0;
}

/* ========== 下拉选择 ========== */
.store-dropdown {
  @apply relative mb-[20px];
}
.store-dropdown__header {
  @apply flex items-center justify-between cursor-pointer rounded-[12px] border-solid border-2 border-transparent;
  background-color: #f6f6f6;
}
.is-pc .store-dropdown__header {
  @apply text-[28px] h-[80px] px-[24px];
}
.is-mobile .store-dropdown__header {
  @apply text-[28px] h-[80px] px-[20px];
}
.store-dropdown__name {
  @apply font-bold;
}
.is-pc .store-dropdown__name {
  @apply text-[28px];
}
.is-mobile .store-dropdown__name {
  @apply text-[36px];
}
.store-dropdown__total {
  @apply flex items-center gap-[6px] cursor-pointer select-none;
  color: #7d7d7d;
}
.store-dropdown--open .store-dropdown__header {
  @apply border-solid border-2 border-[#DDD];
}
.store-dropdown--open .store-dropdown__total {
  color: #000;
}
.store-dropdown__arrow {
  @apply inline-block transition-transform duration-200;
  color: inherit;
}
.store-dropdown__arrow--open {
  @apply rotate-180;
}

.store-dropdown__list {
  @apply absolute right-0 z-10 rounded-[12px] max-h-[290px] overflow-y-auto border-solid border-2 border-[#DDD] py-[20px];
  background-color: #f6f6f6;
  margin-top: 10px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.10);
  scrollbar-width: thin;
  scrollbar-color: #d9d9d9 transparent;
}
.store-dropdown__list::-webkit-scrollbar {
  width: 6px;
}
.store-dropdown__list::-webkit-scrollbar-thumb {
  background-color: #d9d9d9;
  border-radius: 3px;
}
.store-dropdown__list::-webkit-scrollbar-track {
  background: transparent;
}
.is-pc .store-dropdown__list {
  @apply w-[600px] right-[-10px];
}
.is-mobile .store-dropdown__list {
  @apply left-1/2 -translate-x-1/2 w-[690px] max-h-[300px];
}
.is-mobile .store-dropdown__item {
  @apply text-[28px] h-[36px] pl-[40px];
  margin-bottom: 20px;
}
.is-mobile .store-dropdown__check {
  @apply left-[16px] w-[18px] h-[18px];
}
.store-dropdown__item {
  @apply relative flex items-center h-[30px] cursor-pointer text-[24px] pl-[52px];
  color: #7d7d7d;
  margin-bottom: 24px;
}
.store-dropdown__item:last-child {
  margin-bottom: 0;
}
.store-dropdown__item--active {
  color: #000;
}
.store-dropdown__check {
  @apply absolute top-1/2 -translate-y-1/2;
  color: #000;
}
.is-pc .store-dropdown__check {
  @apply left-[16px] w-[20px] h-[20px];
}
.store-dropdown__item-name {
  @apply truncate;
}

/* ========== PC 媒体区 ========== */
.store-media--with-map {
  @apply grid grid-cols-2 gap-[20px] mb-[30px];
}
.store-media--no-map {
  @apply grid grid-cols-2 gap-[24px];
}

.store-media__image {
  @apply w-full overflow-hidden rounded-[4px];
}
.store-media__image img {
  @apply w-full h-full object-cover;
}
.is-pc .store-media__image {
  @apply w-[580px] h-[360px];
}

/* H5 内容区（非 header 部分） */
.store-body-mobile {
  @apply rounded-[12px];
  background-color: #F6F6F6;
}

/* H5 图片 */
.store-media__image--mobile {
  @apply w-[670px] h-[375px] rounded-t-[8px] rounded-b-none mb-[24px] mx-auto;
}
.store-media__image--mobile img {
  @apply w-full h-full object-cover rounded-t-[8px] rounded-b-none;
}

.store-media__map {
  @apply w-full overflow-hidden rounded-[4px];
}
.is-pc .store-media__map {
  @apply w-[580px] h-[360px];
}

/* ========== PC 信息区 ========== */
.store-info--below {
  @apply mb-[20px];
}
.store-info--side {
  @apply flex flex-col justify-center rounded-[8px] px-[24px] gap-[30px];
  background-color: #F6F6F6;
}
.store-info--side-no-btn {
  @apply gap-[50px];
}

/* H5 信息区 */
.store-info--mobile {
  @apply mb-[20px] px-[20px];
}
.store-info--mobile .store-info__row {
  @apply mb-[30px];
}
.store-info--mobile .store-info__row:last-child {
  @apply mb-0;
}

.store-info__row {
  @apply flex items-start gap-[8px] text-[24px] text-gray-700 leading-[1.4];
}
.store-info--side .store-info__row {
  @apply mb-0;
}
.store-info--below .store-info__row {
  @apply mb-[10px];
}
.store-info--below .store-info__row:last-child {
  @apply mb-0;
}
.store-info__icon {
  @apply flex-shrink-0 w-[32px] h-[32px] mt-[1px];
}
.store-info__label {
  @apply font-medium flex-shrink-0;
}
.store-info__text {
  @apply flex-1;
}
.store-info__content {
  @apply flex-1;
}

/* ========== 按钮 ========== */
.store-action--center {
  @apply flex justify-center mt-[50px] mb-0;
}
.store-action--inline {
  @apply mt-0;
}
.store-action--inline .store-action__btn {
  @apply text-[20px];
}
.store-action--mobile {
  @apply my-[50px] flex justify-center;
}
.store-action__btn {
  @apply rounded-full font-medium cursor-pointer transition-opacity hover:opacity-90 border-none;
}
.is-pc .store-action__btn {
  @apply min-w-[200px] px-[40px] py-[12px];
}
.is-pc .store-action--center .store-action__btn {
  @apply w-[360px] h-[60px] text-[20px] p-0;
}
.store-action__btn--mobile {
  @apply w-[540px] h-[100px] mx-auto text-[36px] text-center;
}

/* ========== 暗色主题覆盖 ========== */
.theme-dark {
  background-color: transparent;
}
.theme-dark .store-dropdown__header {
  background-color: #303030;
}
.theme-dark .store-dropdown__total {
  color: #c2c2c2;
}
.theme-dark .store-dropdown--open .store-dropdown__header {
  border-color: #585858;
}
.theme-dark .store-dropdown--open .store-dropdown__total {
  color: #fff;
}
.theme-dark .store-dropdown--open .store-dropdown__arrow {
  color: #fff;
}
.theme-dark .store-dropdown__list {
  background-color: #303030;
  border-color: #585858;
  scrollbar-color: #7d7d7d transparent;
}
.theme-dark .store-dropdown__list::-webkit-scrollbar-thumb {
  background-color: #7d7d7d;
}
.theme-dark .store-dropdown__item {
  color: #aaa;
}
.theme-dark .store-dropdown__item--active {
  color: #fff;
}
.theme-dark .store-dropdown__check {
  color: #fff;
}
.theme-dark .store-info__row {
  color: #fff;
}
.theme-dark .store-info--side {
  background-color: #303030;
}
.theme-dark .store-body-mobile {
  background-color: #303030;
}

/* ========== H5 地图 ========== */
.store-map-mobile {
  @apply w-[630px] h-[390px] mt-[20px] rounded-[8px] overflow-hidden mx-auto;
}
.store-info--mobile + .store-map-mobile {
  @apply mt-[50px];
}
</style>
